import React, {createContext, useState, ReactNode, useContext, useEffect, useRef, useCallback} from 'react';
import { getAuth, signOut } from 'firebase/auth';
import {Activity, Chat, UnreadChat} from "../lib/types";
import {useUserContext} from "./UserContext";
import {useInboxContext} from "./InboxProvider";
 interface ChatListContextType {
  chatList: Chat[];
  chatListVisible: boolean;
  setChatListVisible: (visible: boolean) => void;
  isUnread: (chat: Chat) => boolean;
  title: string | ReactNode;
  setChatListSettings: (title: string | ReactNode, chats?: Chat[] | undefined, activeChatId?: string | null | undefined) => void;
  setLoadChats: (loadChats: (unreadChats: UnreadChat[], activity: Activity) => any) => any;
  updateChatOnList: (chat: Chat) => void;
  fixedChats: Chat[];
  currentFilter: string | null;
  setCurrentFilter: (filter: string | null) => void;
  runRefresh: () => void;
}

 export const ChatListContext = createContext<ChatListContextType | undefined>(undefined);

 interface UserProviderProps {
  children: ReactNode;
}

export const ChatListProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [chatList, setChatListState] = useState<Chat[]>([]);
  const [title, setTitle] = useState<string | ReactNode>('');
  const {inbox} = useInboxContext();
  const { activity, unreadChats, setActiveChatId, activeChatId, user } = useUserContext();
  const [loadChats, setLoadChats] = useState<(unreadChats: UnreadChat[], activity: Activity) => any>(function() {});
  const [chatListVisible, setChatListVisible] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<string | null>(null);
  const [fixedChats, setFixedChats] = useState<Chat[]>([]);
  const isUnread = useCallback((chat: Chat) => {
    return unreadChats.find((unread) => unread.id === chat.id) !== undefined;
  }, [unreadChats, activity]);

  const setChatList = (chats: Chat[]) => {
    setChatListState(chats);
  }

  const updateChatOnList = (chat: Chat) => {
    const updatedChats = chatList.map((c) => {
      if(c.id === chat.id) {
        return chat;
      }
      return c;
    });
    setChatList(updatedChats);
  }

  useEffect(() => {
    if(currentFilter === "inbox") {
      setChatList(inbox);
    }
    if (currentFilter && currentFilter.startsWith("dm_")) {
      const id = currentFilter.replace("dm_", "");
      const chats = inbox.filter(
        chat => {
          const members = Object.keys(chat.userPermissions || {});
          return members.includes(id) && members.includes(user?.uid || "");
        }
      );
      setChatList(chats);
    }
  }, [inbox, currentFilter]);

  // Use useRef to store the timeout reference
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = useCallback((unreadChats: UnreadChat[], activity: Activity, attempt = 0) => {
    // Clear any existing timeout to reset the debounce period
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Set a new timeout to delay the execution
    refreshTimeoutRef.current = setTimeout(async () => {
      if (loadChats && typeof loadChats === "function") {
        const chats = await loadChats(unreadChats, activity);
        setChatList(chats);

        // Check if any chat title includes "generating title"
        if (
          chats.slice(0, 2).some((chat: Chat) =>
            chat.title.toLowerCase().includes("generating title")
          ) &&
          attempt < 10
        ) {
          window.setTimeout(() => {
            refresh(unreadChats, activity, attempt + 1);
          }, 300);
        }
      }
    }, 300); // Wait time in milliseconds
  }, [loadChats, setChatList]);
  useEffect(() => {
    if(unreadChats && activity) {
      refresh(unreadChats || [], activity);
    }
  }, [unreadChats, activity, inbox]);

  const setChatListSettings = (title: string | ReactNode, chats: Chat[] | undefined, activeChatId: string | null | undefined = undefined) => {
    setTitle(title);
    if (chats) {
      setChatList(chats);
    }
    if (activeChatId !== undefined) {
      setActiveChatId(activeChatId || "");
    }
  }

  const runRefresh = useCallback(() => {
    if(unreadChats && activity) {
      refresh(unreadChats || [], activity);
    }
  }, [refresh, unreadChats, activity]);

  return (
    <ChatListContext.Provider value={{runRefresh, currentFilter, setCurrentFilter, fixedChats, updateChatOnList, setLoadChats, title, chatList, chatListVisible, setChatListVisible, isUnread, setChatListSettings }}>
      {children}
    </ChatListContext.Provider>
  );
};

export const useChatListContext = (): ChatListContextType => {
  const context = useContext(ChatListContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};


export const logout = () => {
  const auth = getAuth();
  signOut(auth);
}
